import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../hooks/reduxHooks';
import userService, { UserRecord, CreateUserPayload, UpdateUserPayload } from '../services/userService';
import PageHeader from '../components/PageHeader';

const ROLES = ['admin', 'radiologist', 'reviewer'];
const LOCALES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
];

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBackInApp = Boolean(location.key && location.key !== 'default');
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const isAdmin = currentUser?.role === 'admin';

  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserPayload>({
    username: '', email: '', password: '', first_name: '', last_name: '', role: 'radiologist',
  });
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserRecord | null>(null);
  const [editForm, setEditForm] = useState<UpdateUserPayload>({});
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Delete confirm
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserRecord | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Reset password
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserRecord | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await userService.listUsers();
      setUsers(data);
      setError('');
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin, fetchUsers]);

  // ── Create ──────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setCreateError('');
    setCreateLoading(true);
    try {
      await userService.createUser(createForm);
      setCreateOpen(false);
      setCreateForm({ username: '', email: '', password: '', first_name: '', last_name: '', role: 'radiologist' });
      setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
      fetchUsers();
    } catch (err: unknown) {
      setCreateError((err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────
  const openEdit = (u: UserRecord) => {
    setEditUser(u);
    setEditForm({ email: u.email, first_name: u.first_name, last_name: u.last_name, role: u.role, is_active: u.is_active, locale: u.locale });
    setEditError('');
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setEditError('');
    setEditLoading(true);
    try {
      await userService.updateUser(editUser.id, editForm);
      setEditOpen(false);
      setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
      fetchUsers();
    } catch (err: unknown) {
      setEditError((err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await userService.deleteUser(deleteTarget.id);
      setDeleteOpen(false);
      setSnackbar({ open: true, message: 'User deactivated successfully', severity: 'success' });
      fetchUsers();
    } catch (err: unknown) {
      setSnackbar({
        open: true,
        message: (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to delete user',
        severity: 'error',
      });
      setDeleteOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Reset Password ─────────────────────────────────────────────────
  const handleReset = async () => {
    if (!resetTarget) return;
    setResetError('');
    setResetLoading(true);
    try {
      await userService.resetPassword(resetTarget.id, resetPassword);
      setResetOpen(false);
      setResetPassword('');
      setSnackbar({ open: true, message: 'Password reset successfully', severity: 'success' });
    } catch (err: unknown) {
      setResetError((err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Administrator access required to manage user accounts.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title="User Administration"
        onBack={() => canGoBackInApp ? navigate(-1) : navigate('/')}
        backLabel="Back"
        actions={
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Create User
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Locale</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    {u.username}
                    {u.must_change_password && (
                      <Chip label="Must change pwd" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{u.first_name} {u.last_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.role}
                      size="small"
                      color={u.role === 'admin' ? 'error' : u.role === 'radiologist' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={u.is_active ? 'success' : 'default'}
                      variant={u.is_active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.locale === 'en-GB' ? 'UK' : 'US'}
                      size="small"
                      color={u.locale === 'en-GB' ? 'secondary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(u)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                      <IconButton size="small" onClick={() => { setResetTarget(u); setResetOpen(true); setResetPassword(''); setResetError(''); }}>
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={u.id === currentUser?.id}
                          onClick={() => { setDeleteTarget(u); setDeleteOpen(true); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* ── Create Dialog ──────────────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <TextField margin="dense" label="Username" fullWidth required
            value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} />
          <TextField margin="dense" label="Email" fullWidth required type="email"
            value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
          <TextField margin="dense" label="First Name" fullWidth required
            value={createForm.first_name} onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })} />
          <TextField margin="dense" label="Last Name" fullWidth required
            value={createForm.last_name} onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })} />
          <TextField margin="dense" label="Password" fullWidth required type="password"
            helperText="Min 8 chars, 1 uppercase, 1 lowercase, 1 digit"
            value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
          <TextField margin="dense" label="Role" fullWidth select
            value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
            {ROLES.map((r) => <MenuItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setCreateOpen(false)} disabled={createLoading}>Cancel</Button>
          <Button size="small" onClick={handleCreate} variant="contained" disabled={createLoading || !createForm.username || !createForm.email || !createForm.password}>
            {createLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ────────────────────────────────────────────── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User: {editUser?.username}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <TextField margin="dense" label="Email" fullWidth
            value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
          <TextField margin="dense" label="First Name" fullWidth
            value={editForm.first_name || ''} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
          <TextField margin="dense" label="Last Name" fullWidth
            value={editForm.last_name || ''} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
          <TextField margin="dense" label="Role" fullWidth select
            value={editForm.role || ''} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
            {ROLES.map((r) => <MenuItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>)}
          </TextField>
          <TextField margin="dense" label="Status" fullWidth select
            value={editForm.is_active === false ? 'inactive' : 'active'}
            onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === 'active' })}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField margin="dense" label="Locale" fullWidth select
            value={editForm.locale || 'en-US'}
            onChange={(e) => setEditForm({ ...editForm, locale: e.target.value })}
            helperText="Select regional spelling and terminology preference">
            {LOCALES.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button size="small" onClick={handleEdit} variant="contained" disabled={editLoading}>
            {editLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ─────────────────────────────────────────── */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate <strong>{deleteTarget?.username}</strong>?
            They will no longer be able to log in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button size="small" onClick={handleDelete} variant="contained" color="error" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset Password Dialog ──────────────────────────────────── */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password: {resetTarget?.username}</DialogTitle>
        <DialogContent>
          {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>
            The user will be required to change their password on next login.
          </Alert>
          <TextField
            margin="dense" label="New Password" fullWidth type="password"
            helperText="Min 8 chars, 1 uppercase, 1 lowercase, 1 digit"
            value={resetPassword} onChange={(e) => setResetPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setResetOpen(false)} disabled={resetLoading}>Cancel</Button>
          <Button size="small" onClick={handleReset} variant="contained" disabled={resetLoading || !resetPassword}>
            {resetLoading ? <CircularProgress size={20} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ───────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementPage;
